import React, {useMemo, useState} from 'react';
import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {ArrDrawer} from '../../../../common/arr-drawer/ArrDrawer';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import './VersionHistoryPanel.scss';
import {ProtocolVersionHistoryItem} from '../../../../../api/types/aiReview.type';
import {useGetProtocolVersionHistory} from '../../../../../hooks/ai/review/useReview';
import {formatUTCDateTime} from '../../../../../utils/utils';
import {ToastType} from '../../../../../shared/constants';

export type VersionHistoryPanelProps = {
  isOpen: boolean;
  onClose: () => void;
  protocolId?: number | null;
  onVersionSelect?: (entry: ProtocolVersionHistoryItem) => void;
};

const getNewestFirst = (entries: ProtocolVersionHistoryItem[]) =>
  [...entries].sort(
    (left, right) =>
      new Date(right.modifiedAt).getTime() - new Date(left.modifiedAt).getTime()
  );

export const formatVersionLabel = (versionName: string): string => {
  const normalizedVersionName = versionName.trim();
  const versionNumber = normalizedVersionName.match(
    /^(?:v|version)\s*(\d+)$/i
  )?.[1];

  return versionNumber ? `Version ${versionNumber}` : normalizedVersionName;
};

export const formatModifierName = (modifiedBy: string): string => {
  const normalizedName = modifiedBy.trim();
  if (!normalizedName) return '—';

  const nameParts = normalizedName.split(',');
  if (nameParts.length !== 2) return normalizedName;

  const lastName = nameParts[0].trim();
  const firstName = nameParts[1].trim();
  return firstName && lastName ? `${firstName} ${lastName}` : normalizedName;
};

export const getUpdatedSections = (entry: ProtocolVersionHistoryItem) =>
  [
    entry.isCaseContextModified && 'Case Context',
    entry.isRelevancyProtocolModified && 'Relevance Criteria',
    entry.isIssueCodeModified && 'Issue Codes',
  ]
    .filter(Boolean)
    .join(', ') || '---';

const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  isOpen,
  onClose,
  protocolId,
  onVersionSelect,
}) => {
  const {data, isError, isLoading, refetch} = useGetProtocolVersionHistory(
    protocolId,
    isOpen
  );
  const [showRetryErrorToast, setShowRetryErrorToast] = useState(false);
  const historyEntries = useMemo(
    () => (Array.isArray(data) ? getNewestFirst(data) : []),
    [data]
  );
  const hasVersionHistoryLoadError = isError || !Array.isArray(data);

  const handleRetry = async () => {
    setShowRetryErrorToast(false);
    try {
      const result = await refetch();
      if (result.isError) {
        setShowRetryErrorToast(true);
      }
    } catch {
      setShowRetryErrorToast(true);
    }
  };

  return (
    <Box className="version-history-panel">
      <ArrDrawer
        side="right"
        isOpen={isOpen}
        onClose={onClose}
        size="small"
        title={t('ai.review.versionHistoryPanel.versionTitle')}
        ariaLabel={t('ai.review.versionHistoryPanel.versionTitle')}
        showButton={true}
        showHeader={true}
        className="version-history-panel"
        closeButtonVariant="primary"
        disableResize={true}
      >
        <Box
          component="div"
          aria-label={t('ai.review.versionHistoryPanel.versionTitle')}
        >
          {isLoading ? (
            <Box
              className="version-history-panel__loading"
              data-testid="version-history-loading"
              role="status"
              aria-label={t('common.loading')}
            >
              <ArrLoadingSpinner />
            </Box>
          ) : hasVersionHistoryLoadError ? (
            <Box
              className="version-history-panel__error"
              data-testid="version-history-error"
              role="alert"
              aria-live="assertive"
            >
              <Typography
                component="h3"
                className="typo-text subhead1_size text-headings version-history-panel__error-title"
                data-testid="version-history-error-title"
              >
                {t(
                  'ai.review.versionHistoryPanel.versionHistoryApiFailureTitle'
                )}
              </Typography>
             
              <ArrButton
                variant="primary"
                size="small"
                dataTestId="version-history-error-retry-button"
                onClick={handleRetry}
              >
                {t(
                  'ai.review.versionHistoryPanel.versionHistoryApiFailureRetry'
                )}
              </ArrButton>
            </Box>
          ) : (
            historyEntries.map((entry, index) => {
              const versionLabel = formatVersionLabel(entry.versionName);
              const parentVersionLabel = entry.parentVersionName || '---';
              const isApproved = entry.status.toLowerCase() === 'approved';

              return (
                <Box
                  component="div"
                  key={`${entry.versionId}-${index}`}
                  className="parent-box"
                >
                  <Box className="first-box">
                    <Box className="first-column">
                      <Typography
                        component="button"
                        type="button"
                        className="title-text typo-text subhead5_size version-history-version-link version-history-label-column"
                        data-testid={`version-history-version-${entry.versionId}-${entry.modifiedAt}`}
                        aria-label={versionLabel}
                        onClick={() => onVersionSelect?.(entry)}
                      >
                        {versionLabel}
                      </Typography>
                      <Typography className="version-base-text typo-text body4_size">
                        {t('ai.review.versionHistoryPanel.vesrionBasedOn')}{' '}
                        <span>{parentVersionLabel}</span>
                      </Typography>
                    </Box>
                    <Box className="second-column">
                      <Typography
                        className={`typo-text body4_size version-status ${isApproved ? 'version-status-approved' : 'version-status-save-draft'}`}
                      >
                        {isApproved
                          ? t(
                              'ai.review.versionHistoryPanel.versionStatusApproved'
                            )
                          : t(
                              'ai.review.versionHistoryPanel.versionStatusDraft'
                            )}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="second-box">
                    <Typography className="title-text typo-text label3_size version-history-label version-history-label-column">
                      {t('ai.review.versionHistoryPanel.versionUpdated')}
                    </Typography>
                    <Typography className="base-text typo-text body4_size version-history-value">
                      {getUpdatedSections(entry)}
                    </Typography>
                  </Box>
                  <Box className="second-box">
                    <Typography className="title-text typo-text label3_size version-history-label version-history-label-column">
                      {t('ai.review.versionHistoryPanel.versionModified')}
                    </Typography>
                    <Typography className="base-text typo-text body4_size version-history-value">
                      {formatModifierName(entry.modifiedBy)} <span>.</span>{' '}
                      {formatUTCDateTime(entry.modifiedAt)}
                    </Typography>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </ArrDrawer>
      {showRetryErrorToast && (
        <ArrToastNotification
          message={t(
            'ai.review.versionHistoryPanel.versionHistoryApiFailureToast'
          )}
          onClose={() => setShowRetryErrorToast(false)}
          status={[ToastType.Error]}
        />
      )}
    </Box>
  );
};

export default VersionHistoryPanel;
